import React, { useState, useEffect, useCallback } from "react";
import { withStyles } from "@mui/styles";
import {
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
} from "@mui/material";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import DialogModal from "../dialogModal/DialogModal";
import ErrorMessage from "../errorMessage/ErrorMessage";

import { 
  submitPayment as apiSubmitPayment,
  getPlayerClubPaymentStatus as apiGetPlayerClubPaymentStatus,
} from "../../../services/api";

const styles = (theme) => ({
  section: {
    width: "100%",
    backgroundColor: "#fff",
  },
  methodOption: {
    padding: "12px",
    marginBottom: "10px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    backgroundColor: "#f9f9f9",
  },
  methodOptionSelected: {
    borderColor: "#2CB045",
    backgroundColor: "#f0faf9",
  },
  cardFieldContainer: {
    marginTop: "20px",
    marginBottom: "10px",
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
  },
  infoBox: {
    padding: "12px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    marginTop: "10px",
    marginBottom: "10px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
  },
});

const PAYMENT_METHOD = {
  GOCARDLESS: "gocardless",
  STRIPE_SAVED: "stripe_saved",
  STRIPE_NEW_CARD: "stripe_new_card",
};

function PaymentDialogInner({
  classes,
  eventFee,
  onClose,
  onSuccess,
  context,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [gettingPaymentStatus, setGettingPaymentStatus] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedSavedMethodId, setSelectedSavedMethodId] = useState(null);
  const [cardDetails, setCardDetails] = useState(null);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // Payment status data
  const [goCardlessReady, setGoCardlessReady] = useState(false);
  const [stripePaymentMethods, setStripePaymentMethods] = useState([]);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);

  const clubHasGoCardless = Boolean(context.currentClub?.isGoCardlessSetup);
  const clubHasStripe = Boolean(context.currentClub?.isStripeSetup);

  const checkPaymentStatus = useCallback(async () => {
    if (!eventFee || eventFee.currentFee <= 0 || eventFee.isPaid) {
      setGettingPaymentStatus(false);
      return;
    }

    setGettingPaymentStatus(true);
    const eventFeeId = eventFee.guid || eventFee.Guid || eventFee.id;
    try {
      const data = await apiGetPlayerClubPaymentStatus(
        context.currentPlayer.guid,
        context.currentClub.clubId,
        eventFeeId
      );

      if (!data || data.reason) {
        setPaymentError("Error checking payment status");
        setGettingPaymentStatus(false);
        return;
      }

      if (data.goCardlessReady) setGoCardlessReady(true);
      if (data.stripePaymentMethods?.length > 0) {
        setStripePaymentMethods(data.stripePaymentMethods);
      }
      if (data.stripeClientSecret) setStripeClientSecret(data.stripeClientSecret);

      setGettingPaymentStatus(false);
    } catch (e) {
      console.error(e);
      setPaymentError("Error checking payment details");
      setGettingPaymentStatus(false);
    }
  }, [eventFee, context.currentPlayer.guid, context.currentClub.clubId]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  const handleSubmitPayment = async () => {
    if (!selectedMethod) return;

    setPaymentError("");
    setSubmittingPayment(true);

    try {
      switch (selectedMethod) {
        case PAYMENT_METHOD.GOCARDLESS:
          await submitGoCardlessPayment();
          break;
        case PAYMENT_METHOD.STRIPE_SAVED:
          await submitStripeSavedCard();
          break;
        case PAYMENT_METHOD.STRIPE_NEW_CARD:
          await submitStripeNewCard();
          break;
        default:
          setSubmittingPayment(false);
      }
    } catch (error) {
      setPaymentError(
        typeof error === "string"
          ? error
          : "An error occurred, please try again."
      );
      setSubmittingPayment(false);
    }
  };

  const submitGoCardlessPayment = async () => {
    const eventId = eventFee.event?.guid || eventFee.eventGuid || eventFee.eventId;
    const playerId = eventFee.player?.guid || context.currentPlayer.guid || context.currentPlayer.id;
    
    const response = await apiSubmitPayment({
      eventId,
      targetPlayerId: playerId,
      paymentType: "gocardless",
    });

    if (response.success) {
      setSubmittingPayment(false);
      onSuccess();
    } else if (response.redirectFlowUrl) {
      setSubmittingPayment(false);
      window.location.href = response.redirectFlowUrl;
    } else {
      throw response.reason || "Payment failed";
    }
  };

  const submitStripeSavedCard = async () => {
    const eventId = eventFee.event?.guid || eventFee.eventGuid || eventFee.eventId;
    const playerId = eventFee.player?.guid || context.currentPlayer.guid || context.currentPlayer.id;
    
    const response = await apiSubmitPayment({
      eventId,
      targetPlayerId: playerId,
      paymentType: "stripe",
      paymentMethodId: selectedSavedMethodId,
      stripeConfirmed: true,
    });

    if (response.success) {
      setSubmittingPayment(false);
      onSuccess();
    } else {
      throw response.reason || "Payment failed";
    }
  };

  const submitStripeNewCard = async () => {
    if (!stripe || !elements || !stripeClientSecret) {
      throw new Error("Stripe not initialized");
    }

    const eventId = eventFee.event?.guid || eventFee.eventGuid || eventFee.eventId;
    const playerId = eventFee.player?.guid || context.currentPlayer.guid || context.currentPlayer.id;

    const cardElement = elements.getElement(CardElement);
    
    const { error } = await stripe.confirmCardPayment(stripeClientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: context.currentPlayer?.name || "",
        },
      },
    });

    if (error) {
      throw error.message || "Card payment failed";
    }

    const response = await apiSubmitPayment({
      eventId,
      targetPlayerId: playerId,
      paymentType: "stripe",
      stripeConfirmed: true,
    });

    if (response.success) {
      setSubmittingPayment(false);
      onSuccess();
    } else {
      throw response.reason || "Payment failed";
    }
  };

  const canSubmit = () => {
    if (!selectedMethod) return false;
    if (
      selectedMethod === PAYMENT_METHOD.STRIPE_SAVED &&
      !selectedSavedMethodId
    )
      return false;
    if (selectedMethod === PAYMENT_METHOD.STRIPE_NEW_CARD && !cardDetails?.complete)
      return false;
    return true;
  };

  if (gettingPaymentStatus) {
    return (
      <DialogModal title="Make Payment" onClose={onClose}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
          <Typography style={{ marginTop: "20px" }}>
            Checking payment details...
          </Typography>
        </Box>
      </DialogModal>
    );
  }

  return (
    <DialogModal
      title="Make Payment"
      onClose={onClose}
      onSubmit={handleSubmitPayment}
      submitText="Pay Now"
      submitting={submittingPayment}
      submitDisabled={!canSubmit() || submittingPayment}
    >
      <Box>
        {/* Amount */}
        <Typography variant="h6" style={{ marginBottom: "20px", color: "#2CB045" }}>
          £{eventFee?.currentFee?.toFixed(2) || "0.00"}
        </Typography>

        {/* Payment Method Selection */}
        <Typography variant="subtitle2" style={{ marginBottom: "10px" }}>
          Select Payment Method
        </Typography>

        <RadioGroup
          value={selectedMethod}
          onChange={(e) => {
            setSelectedMethod(e.target.value);
            setSelectedSavedMethodId(null);
            setPaymentError("");
          }}
        >
          {/* GoCardless */}
          {clubHasGoCardless && (
            <FormControlLabel
              value={PAYMENT_METHOD.GOCARDLESS}
              control={<Radio />}
              label={
                goCardlessReady
                  ? "Direct Debit (GoCardless)"
                  : "Set Up Direct Debit"
              }
            />
          )}

          {/* Stripe - Saved Cards */}
          {(clubHasStripe || stripeClientSecret) &&
            stripePaymentMethods.map((pm) => (
              <FormControlLabel
                key={pm.paymentMethodId || pm.id}
                value={PAYMENT_METHOD.STRIPE_SAVED}
                control={<Radio />}
                label={`${pm.brand?.charAt(0).toUpperCase() + pm.brand?.slice(1) || "Card"} •••• ${pm.last4}`}
                onChange={() => {
                  setSelectedMethod(PAYMENT_METHOD.STRIPE_SAVED);
                  setSelectedSavedMethodId(pm.paymentMethodId);
                  setPaymentError("");
                }}
              />
            ))}

          {/* Stripe - New Card */}
          {(clubHasStripe || stripeClientSecret) && (
            <FormControlLabel
              value={PAYMENT_METHOD.STRIPE_NEW_CARD}
              control={<Radio />}
              label="New Credit/Debit Card"
            />
          )}
        </RadioGroup>

        {/* Stripe Card Entry */}
        {selectedMethod === PAYMENT_METHOD.STRIPE_NEW_CARD && (
          <Box className={classes.cardFieldContainer}>
            <CardElement
              onChange={(e) => setCardDetails(e)}
              options={{
                style: {
                  base: {
                    fontSize: "14px",
                    color: "#333",
                  },
                },
              }}
            />
          </Box>
        )}

        {/* Info Messages */}
        {selectedMethod === PAYMENT_METHOD.GOCARDLESS && !goCardlessReady && (
          <Box className={classes.infoBox}>
            <Typography variant="body2">
              You'll be redirected to authorize Direct Debit, then can return to complete payment.
            </Typography>
          </Box>
        )}

        {/* Error Message */}
        {paymentError && <ErrorMessage message={paymentError} />}
      </Box>
    </DialogModal>
  );
}

export default withStyles(styles)(PaymentDialogInner);
